import { useState } from "react"

import { parseErrorAsObj } from "../../../../../errors/errorProcessors"
import { IAppError } from "../../../../../errors/errorTypes"
import { snackbar } from "../../../../../utils/general/generalUtils"
import { postClientProtected } from "../../../clients/clients"
import { DNO_SOL_EXPORT_MPAN } from "../../../urls/hybrid"

interface IPostDnoSolExportMpanHook {
    isSending: boolean
    error: IAppError | undefined
    postDnoSolExportMpan: (
        notificationId: string,
        exportMpan: string,
        onSuccess: () => void
    ) => Promise<void>
}

const getDnoSolExportMpanErrorMessage = (appError: IAppError): string => {
    const backendMessage = appError.displayDetail || appError.message

    if (backendMessage.includes("Error assigning export MPAN")) {
        return "Error assigning export MPAN"
    }

    if (backendMessage.includes("Notification does not exist")) {
        return "Notification does not exist"
    }

    if (
        backendMessage.includes("You cannot assign an export MPAN for this DNO")
    ) {
        return "You cannot assign an export MPAN for this DNO"
    }

    if (
        backendMessage.includes(
            "Only current notifications can receive an export MPAN"
        )
    ) {
        return "Only current notifications can receive an export MPAN"
    }

    const invalidDnoMpanMessage = backendMessage.match(
        /The export MPAN supplied is not valid for this .* notification/
    )

    if (invalidDnoMpanMessage) {
        return invalidDnoMpanMessage[0]
    }

    return backendMessage
}

const usePostDnoSolExportMpan = (): IPostDnoSolExportMpanHook => {
    const [isSending, setIsSending] = useState(false)
    const [error, setError] = useState<IAppError | undefined>(undefined)

    const postDnoSolExportMpan = async (
        notificationId: string,
        exportMpan: string,
        onSuccess: () => void
    ): Promise<void> => {
        try {
            setIsSending(true)
            setError(undefined)

            await postClientProtected(DNO_SOL_EXPORT_MPAN, {
                notificationId,
                exportMpan,
            })
            snackbar("Export MPAN submitted successfully", "success")
            onSuccess()
        } catch (err) {
            const appError = parseErrorAsObj(err)
            setError(appError)
            snackbar(getDnoSolExportMpanErrorMessage(appError), "error")
        } finally {
            setIsSending(false)
        }
    }

    return {
        isSending,
        error,
        postDnoSolExportMpan,
    }
}

export default usePostDnoSolExportMpan
